import React, { createContext, useContext, useState, useEffect } from 'react';
import { studentsFromApiResponse } from '../utils/studentDisplay';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// API base URL - use relative path for Vercel/Netlify, absolute for local dev
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

// Safe localStorage wrapper for Chrome compatibility
const safeLocalStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('localStorage not available:', error);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn('localStorage setItem failed:', error);
      // Chrome sometimes blocks localStorage in certain contexts
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('localStorage removeItem failed:', error);
    }
  }
};

// Must exceed server-side Mongo serverSelectionTimeout (~15s) + cold start, or the client
// aborts first and shows a misleading "backend not running" message on production.
const API_REQUEST_TIMEOUT_MS = 28000;

const isProductionBuild = process.env.NODE_ENV === 'production';

// Helper function to make authenticated requests with timeout and retry logic
const apiRequest = async (url, options = {}, retries = 2) => {
  const token = safeLocalStorage.getItem('token');
  
  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_REQUEST_TIMEOUT_MS);
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    signal: controller.signal,
    ...options,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${url}`, config);
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      let errorMessage = 'Request failed';
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch (e) {
        // If response is not JSON, use status text
        errorMessage = response.statusText || `Server error (${response.status})`;
      }
      throw new Error(errorMessage);
    }
    
    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    
    // Retry on network errors if retries remaining
    if (retries > 0 && (error.name === 'AbortError' || (error instanceof TypeError && error.message.includes('fetch')))) {
      // Wait 500ms before retry
      await new Promise(resolve => setTimeout(resolve, 500));
      return apiRequest(url, options, retries - 1);
    }
    
    // Handle network errors
    if (error.name === 'AbortError') {
      throw new Error(
        isProductionBuild
          ? 'The request timed out. The server may be starting up or the database slow to respond. Please try again in a moment.'
          : 'Request timed out. The backend server may not be running. Please start the backend server (see QUICK_START.md) and try again.'
      );
    }
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(
        isProductionBuild
          ? 'Could not reach the server. Check your connection, or try again later.'
          : 'Cannot connect to backend server. Please make sure the backend server is running on port 5000 (see QUICK_START.md for instructions).'
      );
    }
    throw error;
  }
};

/** Align API user payloads into a consistent shape (incl. `id`). */
const normalizeUserFromApi = (u) => {
  if (!u) return null;
  const id = u.id != null ? u.id : u._id;
  return { ...u, id };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated on app load (non-blocking)
  useEffect(() => {
    const checkAuth = async () => {
      // Set loading to false immediately so page can render
      setLoading(false);
      
      const token = safeLocalStorage.getItem('token');
      if (token) {
        // Verify auth in background (non-blocking) with timeout
        try {
          const data = await apiRequest('/auth/verify');
          setUser(normalizeUserFromApi(data.user));
        } catch (error) {
          console.error('Auth verification failed:', error);
          // Only remove token on clear auth errors, not network errors
          if (!error.message.includes('timeout') && !error.message.includes('Network error')) {
            safeLocalStorage.removeItem('token');
          }
        }
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const data = await apiRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      safeLocalStorage.setItem('token', data.token);
      const profile = await apiRequest('/auth/me');
      const normalized = normalizeUserFromApi(profile.user || data.user);
      setUser(normalized);
      return { success: true, user: normalized };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const register = async (userData) => {
    try {
      const data = await apiRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      });

      safeLocalStorage.setItem('token', data.token);
      const profile = await apiRequest('/auth/me');
      const normalized = normalizeUserFromApi(profile.user || data.user);
      setUser(normalized);
      return { success: true, user: normalized };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    safeLocalStorage.removeItem('token');
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    try {
      const data = await apiRequest('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });

      setUser(normalizeUserFromApi(data.user));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const refreshUser = async () => {
    try {
      const data = await apiRequest('/auth/me');
      const next = normalizeUserFromApi(data.user);
      setUser(next);
      return { success: true, user: next };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const getStudents = async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters);
      const payload = await apiRequest(`/users/students?${queryParams}`);
      const students = studentsFromApiResponse(payload);
      return {
        success: true,
        data: students,
        pagination: payload && typeof payload === 'object' ? payload.pagination : undefined,
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const getStudentProfile = async (studentId) => {
    try {
      const data = await apiRequest(`/users/students/${studentId}`);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const createBooking = async (bookingData) => {
    try {
      const data = await apiRequest('/bookings', {
        method: 'POST',
        body: JSON.stringify(bookingData),
      });

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const getMyBookings = async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters);
      const data = await apiRequest(`/bookings/my-bookings?${queryParams}`);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const getStudentBookings = async (studentId, query = {}) => {
    try {
      const queryParams = new URLSearchParams(query);
      const qs = queryParams.toString();
      const url = qs
        ? `/bookings/student/${studentId}?${qs}`
        : `/bookings/student/${studentId}`;
      const data = await apiRequest(url);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const sendBookingMessage = async (bookingId, message) => {
    try {
      const data = await apiRequest(`/bookings/${bookingId}/message`, {
        method: 'POST',
        body: JSON.stringify({ message }),
      });

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateBookingStatus = async (bookingId, status, reason = '') => {
    try {
      const data = await apiRequest(`/bookings/${bookingId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status, reason }),
      });

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const addReview = async (bookingId, rating, comment = '') => {
    try {
      const data = await apiRequest(`/bookings/${bookingId}/review`, {
        method: 'POST',
        body: JSON.stringify({ rating, comment }),
      });

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateAvailability = async (availability) => {
    try {
      const data = await apiRequest('/users/availability', {
        method: 'PUT',
        body: JSON.stringify({ availability }),
      });
      setUser((prev) =>
        prev
          ? {
              ...prev,
              availability: data?.availability ?? availability,
            }
          : prev
      );

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateUnavailableDates = async (dates) => {
    try {
      const data = await apiRequest('/users/unavailable-dates', {
        method: 'PUT',
        body: JSON.stringify({ dates }),
      });
      setUser((prev) =>
        prev
          ? {
              ...prev,
              unavailableDates: data?.unavailableDates ?? [],
            }
          : prev
      );

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const addCertification = async (certification) => {
    try {
      const data = await apiRequest('/users/certifications', {
        method: 'POST',
        body: JSON.stringify({ certification }),
      });

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    refreshUser,
    getStudents,
    getStudentProfile,
    createBooking,
    getMyBookings,
    getStudentBookings,
    sendBookingMessage,
    updateBookingStatus,
    addReview,
    updateAvailability,
    updateUnavailableDates,
    addCertification,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
